import { Component } from '@theme/component';

/**
 * <ms-mobile-menu-component>
 *
 * Menú móvil visual (N2) que se INYECTA dentro del drawer NATIVO de Horizon para
 * que la hamburguesa actual del header lo abra. NO crea un segundo drawer: mueve su
 * panel (.ms-mobile-menu__panel) dentro de `header-drawer .menu-drawer` y oculta el
 * contenido nativo vía la clase `ms-has-custom-menu`. Reusa abrir/cerrar/backdrop/
 * scroll-lock/focus-trap nativos: el cierre y el backdrop usan `header-drawer/close`
 * y el buscador `#search-modal/showDialog`.
 *
 * CLAVE (storefront vs editor): el header se HIDRATA en la tienda (Section Rendering
 * API), NO en el editor. Esa hidratación hace un `morph` del drawer; como `morph`
 * recorre los hijos aunque el contenedor tenga `data-skip-node-update`, ELIMINA el
 * panel inyectado (no existe en el HTML del servidor) y vuelve al menú nativo. Por eso
 * "funcionaba en el editor pero no en mobile". Solución: observar el drawer y
 * REINYECTAR si la hidratación quita el panel (idempotente, sin bucles).
 *
 * Robusto / aditivo (mandato "nada se rompe"):
 *  - Funciona en cualquier grupo/orden (header-group, footer-group o template): si el
 *    drawer aún no está en el DOM, espera a que aparezca.
 *  - Reinyecta tras la hidratación del header.
 *  - En el editor el drawer se abre SOLO al seleccionar ESTA sección
 *    (shopify:section:select) y se cierra al deseleccionarla. NUNCA auto-abre en
 *    load: el editor recarga el iframe seguido y el editorStateManager nativo
 *    (theme-editor.js) re-abre cualquier overlay que quede con `open`, así que un
 *    auto-open se volvía "drawer siempre abierto" + preview trabado.
 *    Re-render del editor dedup por data-ms-mm-id. Varias instancias → solo una inyecta.
 *  - Sin drawer / sin JS → el panel queda inline (degradación).
 *  - Limpia al desconectarse.
 *
 * Patrón ms- (PATTERNS.md): extiende Component (@theme/component), registro
 * idempotente, tag terminado en -component. No toca el core.
 *
 * @extends {Component}
 */

const DRAWER_SELECTOR = 'header-drawer .menu-drawer';

class MsMobileMenu extends Component {
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {Element | null} */
  #drawer = null;
  #injected = false;
  /** @type {MutationObserver | null} */
  #docObserver = null;
  /** @type {MutationObserver | null} */
  #drawerObserver = null;
  /** @type {number | null} */
  #docTimer = null;

  connectedCallback() {
    super.connectedCallback();

    const panel = this.querySelector('.ms-mobile-menu__panel');
    if (panel instanceof HTMLElement) this.#panel = panel;

    // Editor: drawer visible solo mientras esta sección esté seleccionada.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
    }

    if (this.#ensureInjected()) return;

    // El header/drawer puede no estar aún en el DOM (orden de secciones / hidratación):
    // espera a que aparezca, con tope de seguridad.
    this.#docObserver = new MutationObserver(() => {
      if (this.#ensureInjected()) this.#stopDocObserver();
    });
    this.#docObserver.observe(document.documentElement, { childList: true, subtree: true });
    this.#docTimer = window.setTimeout(() => this.#stopDocObserver(), 10000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    this.#stopDocObserver();
    if (this.#drawerObserver) {
      this.#drawerObserver.disconnect();
      this.#drawerObserver = null;
    }
    this.#restore();
  }

  #stopDocObserver() {
    if (this.#docObserver) {
      this.#docObserver.disconnect();
      this.#docObserver = null;
    }
    if (this.#docTimer) {
      clearTimeout(this.#docTimer);
      this.#docTimer = null;
    }
  }

  /**
   * Asegura (idempotente) que el panel esté dentro del drawer.
   * @returns {boolean} true si quedó inyectado.
   */
  #ensureInjected = () => {
    const panel = this.#panel ?? this.querySelector('.ms-mobile-menu__panel');
    if (!(panel instanceof HTMLElement)) return false;
    this.#panel = panel;

    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return false;

    const id = panel.getAttribute('data-ms-mm-id') || '';
    const existing = drawer.querySelector('.ms-mobile-menu__panel');
    if (existing && existing !== panel) {
      if (existing.getAttribute('data-ms-mm-id') === id) {
        existing.remove(); // panel obsoleto de un re-render de ESTA sección (editor)
      } else {
        // Otra instancia distinta ya es dueña del drawer → no duplicar.
        this.classList.add('is-injected');
        this.#injected = true;
        return true;
      }
    }

    const already = drawer.contains(panel) && drawer.classList.contains('ms-has-custom-menu');
    if (!already) {
      drawer.prepend(panel);
      drawer.classList.add('ms-has-custom-menu');
    }
    this.classList.add('is-injected');
    this.#drawer = drawer;
    this.#injected = true;

    // Observa el drawer para REINYECTAR si la hidratación (morph) lo quita.
    this.#watchDrawer(drawer);

    return true;
  };

  /**
   * Observa el contenedor del drawer; si un morph de hidratación quita el panel o la
   * clase, los restablece. Sin bucles: tras restablecer, la condición ya no se cumple.
   * @param {Element} drawer
   */
  #watchDrawer(drawer) {
    if (this.#drawerObserver) return;
    const container = drawer.closest('details') ?? drawer;
    this.#drawerObserver = new MutationObserver(() => {
      const panel = this.#panel;
      if (!panel) return;
      const current = document.querySelector(DRAWER_SELECTOR);
      if (!current) return;
      if (!current.contains(panel) || !current.classList.contains('ms-has-custom-menu')) {
        this.#drawer = current;
        current.prepend(panel);
        current.classList.add('ms-has-custom-menu');
      }
    });
    this.#drawerObserver.observe(container, { childList: true, subtree: true });
  }

  /**
   * Editor: abre el drawer al seleccionar ESTA sección (su wrapper contiene a `this`;
   * el panel se mueve al drawer, pero el custom element se queda en la sección).
   * @param {Event} event
   */
  #onEditorSelect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) {
      this.#setEditorDrawer(true);
    }
  };

  /** @param {Event} event */
  #onEditorDeselect = (event) => {
    if (event.target instanceof HTMLElement && event.target.contains(this)) {
      this.#setEditorDrawer(false);
    }
  };

  /**
   * Abre/cierra el drawer nativo por su API (mismo camino que el editorStateManager
   * de theme-editor.js): `open()` + atributo en details; `close()` resetea
   * open/aria/focus-trap solo. Idempotente: no repite si ya está en ese estado.
   * @param {boolean} open
   */
  #setEditorDrawer(open) {
    const drawerEl = /** @type {any} */ (document.querySelector('header-drawer'));
    if (!drawerEl) return;
    const details =
      drawerEl.querySelector('#Details-menu-drawer-container') ?? drawerEl.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details.hasAttribute('open') === open) return;

    if (open) {
      if (typeof drawerEl.open === 'function') {
        drawerEl.open();
      } else {
        details.classList.add('menu-open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      }
      details.setAttribute('open', '');
    } else if (typeof drawerEl.close === 'function') {
      drawerEl.close();
    } else {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }
  }

  /** Devuelve el panel a la sección y limpia el drawer. */
  #restore() {
    if (!this.#injected) return;
    const drawer = this.#drawer;
    if (drawer && this.#panel && drawer.contains(this.#panel)) {
      drawer.classList.remove('ms-has-custom-menu');
      this.appendChild(this.#panel);
    }
    this.classList.remove('is-injected');
    this.#drawer = null;
    this.#injected = false;
  }
}

if (!customElements.get('ms-mobile-menu-component')) {
  customElements.define('ms-mobile-menu-component', MsMobileMenu);
}
